// src/belediye/components/reports/ReportsExport.js
// Logic tamamen korundu — sadece UI katmanı yeniden tasarlandı.
// Modal: sade, beyaz panel, section başlıkları uppercase küçük, seçim kartları

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  exportReportV1Xlsx,
  selectReportV1CanExport,
  selectReportV1Export,
  selectReportV1Query,
  selectReportV1IsPresidentLike,
  selectReportV1AdvancedOpen,
} from "../../redux/slices/belediyeReportV1Slice";

import "./ReportsExport.css";

/* =============================================================================
   Debug
============================================================================= */

const DEBUG =
  (typeof process !== "undefined" &&
    process?.env?.NODE_ENV !== "production" &&
    process?.env?.REACT_APP_REPORT_DEBUG !== "0") ||
  false;

const log = (...args) => DEBUG && console.log("[ReportsExport]", ...args);

/* =============================================================================
   Sabitler / Yardımcılar
============================================================================= */

const DS = {
  APPLICATIONS:          "applications",
  EVENTS:                "events",
  STATUS_LOGS:           "status_logs",
  FORWARD_LOGS:          "forward_logs",
  OPERATION_LOGS:        "operation_logs",
  CLOSE_REQUEST_HISTORY: "close_request_history",
};

const EXPORT_MODE = {
  APPLICATIONS_ONLY: "applications_only",
  FULL:              "full",
};

const ensureArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const uniq = (arr) => {
  const s = new Set();
  const out = [];
  ensureArray(arr).forEach((x) => {
    const k = x === null || x === undefined ? "" : String(x);
    if (!k || s.has(k)) return;
    s.add(k);
    out.push(String(x));
  });
  return out;
};

const isAdvancedDataset = (ds) =>
  ds === DS.EVENTS ||
  ds === DS.STATUS_LOGS ||
  ds === DS.FORWARD_LOGS ||
  ds === DS.OPERATION_LOGS ||
  ds === DS.CLOSE_REQUEST_HISTORY;

const datasetLabelTr = (ds) => {
  switch (ds) {
    case DS.APPLICATIONS:          return "Başvurular";
    case DS.EVENTS:                return "Olaylar";
    case DS.STATUS_LOGS:           return "Durum Logları";
    case DS.FORWARD_LOGS:          return "Yönlendirme Logları";
    case DS.OPERATION_LOGS:        return "İşlem Logları";
    case DS.CLOSE_REQUEST_HISTORY: return "Kapatma Talep Geçmişi";
    default:                       return ds || "Sheet";
  }
};

function buildAvailableDatasetsFromQuery({ isPresidentLike, advancedOpen, lastQueryParams }) {
  if (!isPresidentLike) return [DS.APPLICATIONS];
  if (!advancedOpen)    return [DS.APPLICATIONS];

  const qDatasets = uniq(ensureArray(lastQueryParams?.datasets));
  const out = new Set([DS.APPLICATIONS]);

  qDatasets.forEach((d) => {
    if (d === DS.APPLICATIONS || isAdvancedDataset(d)) out.add(d);
  });

  if ((lastQueryParams?.view_mode || "applications") === "events") out.add(DS.EVENTS);

  return Array.from(out);
}

function computeExportOverrides({ isPresidentLike, advancedOpen, selectedDatasets }) {
  if (!isPresidentLike || !advancedOpen) {
    return { export_mode: EXPORT_MODE.APPLICATIONS_ONLY, datasets: [DS.APPLICATIONS] };
  }

  const ds       = uniq(selectedDatasets);
  const effective = ds.length ? ds : [DS.APPLICATIONS];
  const withApps  = effective.includes(DS.APPLICATIONS)
    ? effective
    : [DS.APPLICATIONS, ...effective];

  const onlyApps = withApps.length === 1 && withApps[0] === DS.APPLICATIONS;

  return {
    export_mode: onlyApps ? EXPORT_MODE.APPLICATIONS_ONLY : EXPORT_MODE.FULL,
    datasets:    withApps,
  };
}

/* =============================================================================
   İkon bileşenleri (inline SVG — dış bağımlılık yok)
============================================================================= */

const IconDownload = () => (
  <svg className="rexp-trigger__icon" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconX = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconAlert = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

/* =============================================================================
   ReportsExport
============================================================================= */

export default function ReportsExport({
  activeDataset       = null,
  availableDatasets: availableDatasetsProp = null,
}) {
  const dispatch = useDispatch();

  const canExport       = useSelector(selectReportV1CanExport);
  const exportState     = useSelector(selectReportV1Export);
  const queryState      = useSelector(selectReportV1Query);
  const isPresidentLike = useSelector(selectReportV1IsPresidentLike);
  const advancedOpen    = useSelector(selectReportV1AdvancedOpen);

  const lastQueryParams = queryState?.lastParams || null;
  const exportLoading   = !!exportState?.loading;
  const exportError     = exportState?.error    || null;
  const exportPercent   = Number(exportState?.percent) || 0;
  const exportMessage   = exportState?.message  || "";
  const exportStartedAt = exportState?.startedAt || null;

  /* ── Geri sayım (10 dk üst sınır) — saniyede bir tik ── */
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!exportLoading) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [exportLoading]);

  const remainingLabel = useMemo(() => {
    if (!exportLoading || !exportStartedAt) return "";
    const deadline = exportStartedAt + 66 * 60 * 1000;
    const sec = Math.max(0, Math.ceil((deadline - nowTick) / 1000));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }, [exportLoading, exportStartedAt, nowTick]);

  /* ── Kullanılabilir dataset listesi ── */
  const availableDatasets = useMemo(() => {
    const base =
      Array.isArray(availableDatasetsProp) && availableDatasetsProp.length
        ? availableDatasetsProp
        : buildAvailableDatasetsFromQuery({ isPresidentLike, advancedOpen, lastQueryParams });

    if (activeDataset && !base.includes(activeDataset)) return [activeDataset, ...base];
    return base;
  }, [availableDatasetsProp, isPresidentLike, advancedOpen, lastQueryParams, activeDataset]);

  /* ── UI state ── */
  const [modalOpen,           setModalOpen]           = useState(false);
  const [mode,                setMode]                = useState("single");
  const [singleDataset,       setSingleDataset]       = useState(DS.APPLICATIONS);
  const [selectedAllDatasets, setSelectedAllDatasets] = useState([DS.APPLICATIONS]);
  const [reportName,          setReportName]          = useState("");

  /* ── Akıllı rapor adı önerisi (tarih aralığı + rapor türü) ── */
  const suggestedReportName = useMemo(() => {
    const isFull = isPresidentLike && advancedOpen && mode === "all";
    const base = isFull ? "Detaylı Başvuru Raporu" : "Başvuru Raporu";

    const fmtDate = (v) => {
      if (!v) return "";
      try {
        const s = String(v).split("T")[0].split(" ")[0]; // ISO tarih kısmı
        const [y, m, d] = s.split("-");
        if (y && m && d) return `${d.padStart(2, "0")}.${m.padStart(2, "0")}.${y}`;
      } catch { /* yut */ }
      return "";
    };

    const from = fmtDate(lastQueryParams?.date_from);
    const to = fmtDate(lastQueryParams?.date_to);
    if (from && to) return `${base} ${from}-${to}`;
    return base;
  }, [isPresidentLike, advancedOpen, mode, lastQueryParams]);

  /* Modal açılınca varsayılanları kur */
  useEffect(() => {
    if (!modalOpen) return;

    const preferredSingle   = activeDataset || DS.APPLICATIONS;
    const normalizedSingle  = availableDatasets.includes(preferredSingle)
      ? preferredSingle
      : (availableDatasets[0] || DS.APPLICATIONS);

    setSingleDataset(normalizedSingle);

    const defaultAll = isPresidentLike && advancedOpen ? availableDatasets : [DS.APPLICATIONS];
    setSelectedAllDatasets(defaultAll);

    // Modal her açılışında rapor adını akıllı öneriyle doldur (kullanıcı düzenleyebilir)
    setReportName(suggestedReportName);

    log("modal:open", { normalizedSingle, defaultAll, availableDatasets });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  /* ── Computed ── */
  const canUseAllMode = isPresidentLike && advancedOpen;

  const normalizedSingleDataset = useMemo(() => {
    const preferred = activeDataset || singleDataset || DS.APPLICATIONS;
    return availableDatasets.includes(preferred) ? preferred : (availableDatasets[0] || DS.APPLICATIONS);
  }, [availableDatasets, activeDataset, singleDataset]);

  const normalizedAllDatasets = useMemo(
    () => uniq(selectedAllDatasets).filter((d) => availableDatasets.includes(d)),
    [selectedAllDatasets, availableDatasets]
  );

  const effectiveSelectedDatasets = useMemo(
    () => mode === "all" ? normalizedAllDatasets : [normalizedSingleDataset],
    [mode, normalizedAllDatasets, normalizedSingleDataset]
  );

  const confirmDisabled = useMemo(() => {
    if (!canExport)    return true;
    if (exportLoading) return true;
    if (!reportName.trim()) return true; // rapor adı zorunlu
    if (mode === "all" && (!canUseAllMode || effectiveSelectedDatasets.length === 0)) return true;
    return false;
  }, [canExport, exportLoading, reportName, mode, canUseAllMode, effectiveSelectedDatasets.length]);

  const triggerTitle = useMemo(() => {
    if (!canExport)    return "Önce filtreleri uygulayın";
    if (exportLoading) return "İndiriliyor…";
    return "Excel olarak indir";
  }, [canExport, exportLoading]);

  /* ── Handlers ── */
  const openModal  = () => { if (!canExport || exportLoading) return; setModalOpen(true); };
  const closeModal = () => { if (exportLoading) return; setModalOpen(false); };

  const toggleAllDataset = (ds) => {
    setSelectedAllDatasets((prev) => {
      const cur = new Set(ensureArray(prev).map(String));
      cur.has(ds) ? cur.delete(ds) : cur.add(ds);
      return Array.from(cur);
    });
  };

  const onConfirm = async () => {
    if (confirmDisabled) return;

    const overrides = computeExportOverrides({
      isPresidentLike,
      advancedOpen,
      selectedDatasets: effectiveSelectedDatasets,
    });

    log("export:confirm", { mode, overrides, effectiveSelectedDatasets, reportName });

    const action = await dispatch(
      exportReportV1Xlsx({ filters: overrides, downloadName: reportName })
    );
    if (exportReportV1Xlsx.fulfilled.match(action)) closeModal();
  };

  /* ── Render ── */
  return (
    <div className="rexp">

      {/* ── Trigger butonu ── */}
      <button
        type="button"
        className={`rexp-trigger${exportLoading ? " rexp-trigger--loading" : ""}`}
        onClick={openModal}
        disabled={!canExport || exportLoading}
        title={triggerTitle}
        aria-label={triggerTitle}
      >
        {exportLoading ? (
          <>
            <span className="rexp-spinner" aria-hidden="true" />
            <span>İndiriliyor…</span>
          </>
        ) : (
          <>
            <IconDownload />
            <span>Excel İndir</span>
          </>
        )}
      </button>

      {/* ── Modal ── */}
      {modalOpen && (
        <div className="rexp-modal" role="dialog" aria-modal="true" aria-label="Excel İndir">

          {/* Backdrop */}
          <div className="rexp-modal__backdrop" onClick={closeModal} />

          {/* Panel */}
          <div className="rexp-modal__panel">

            {/* ── Head ── */}
            <div className="rexp-modal__head">
              <div className="rexp-modal__head-left">
                <div className="rexp-modal__title">Excel İndir</div>
                <div className="rexp-modal__sub">
                  {!isPresidentLike
                    ? "Birim personeli için sadece Başvurular tablosu indirilebilir."
                    : !advancedOpen
                      ? "Basit modda sadece Başvurular tablosu indirilebilir."
                      : "İndirilecek tabloları seçin."}
                </div>
              </div>

              <button
                type="button"
                className="rexp-modal__close"
                onClick={closeModal}
                disabled={exportLoading}
                aria-label="Kapat"
              >
                <IconX size={14} />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="rexp-modal__body">

              {/* Rapor adı (zorunlu) */}
              <div className="rexp-section">
                <div className="rexp-section__label">
                  Rapor Adı <span className="rexp-req">*</span>
                </div>
                <input
                  type="text"
                  className={`rexp-input${!reportName.trim() ? " is-invalid" : ""}`}
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="Örn. Başvuru Raporu 01.01.2026-31.01.2026"
                  disabled={exportLoading}
                  maxLength={120}
                  aria-label="Rapor adı"
                />
                <div className="rexp-hint">
                  {reportName.trim()
                    ? "Dosya bu adla indirilecek."
                    : "Bir rapor adı girin — dosya bu adla indirilir."}
                </div>
              </div>

              {/* İndirme türü */}
              <div className="rexp-section">
                <div className="rexp-section__label">İndirme Türü</div>

                <div className="rexp-radio-row">
                  <label className="rexp-radio">
                    <input
                      type="radio"
                      name="rexp_mode"
                      value="single"
                      checked={mode === "single"}
                      onChange={() => setMode("single")}
                      disabled={exportLoading}
                    />
                    <span>Tek tablo</span>
                  </label>

                  <label className={`rexp-radio${!canUseAllMode ? " is-disabled" : ""}`}>
                    <input
                      type="radio"
                      name="rexp_mode"
                      value="all"
                      checked={mode === "all"}
                      onChange={() => setMode("all")}
                      disabled={exportLoading || !canUseAllMode}
                    />
                    <span>Çoklu tablo</span>
                  </label>
                </div>

                {!canUseAllMode && (
                  <div className="rexp-hint">
                    Çoklu tablo indirme yalnızca başkanlık + gelişmiş modda kullanılabilir.
                  </div>
                )}
              </div>

              {/* Tek tablo seçimi */}
              {mode === "single" && (
                <div className="rexp-section">
                  <div className="rexp-section__label">Tablo</div>

                  {activeDataset ? (
                    <div className="rexp-single-fixed">
                      {datasetLabelTr(normalizedSingleDataset)}
                    </div>
                  ) : (
                    <select
                      className="rexp-select"
                      value={normalizedSingleDataset}
                      onChange={(e) => setSingleDataset(e.target.value)}
                      disabled={exportLoading}
                    >
                      {availableDatasets.map((ds) => (
                        <option key={ds} value={ds}>
                          {datasetLabelTr(ds)}
                        </option>
                      ))}
                    </select>
                  )}

                  <div className="rexp-hint">
                    {normalizedSingleDataset === DS.APPLICATIONS
                      ? "Başvurular tablosu hızlı indirme modunda indirilir."
                      : "Log ve geçmiş tabloları tam export modunda indirilir — satır limiti uygulanabilir."}
                  </div>
                </div>
              )}

              {/* Çoklu tablo seçimi */}
              {mode === "all" && (
                <div className="rexp-section">
                  <div className="rexp-section__label">Tablolar</div>

                  {!canUseAllMode ? (
                    <div className="rexp-hint">
                      Bu seçenek için gelişmiş mod açık olmalıdır.
                    </div>
                  ) : (
                    <div className="rexp-checklist">
                      {availableDatasets.map((ds) => (
                        <label key={ds} className="rexp-check">
                          <input
                            type="checkbox"
                            checked={normalizedAllDatasets.includes(ds)}
                            onChange={() => toggleAllDataset(ds)}
                            disabled={exportLoading}
                          />
                          <span>{datasetLabelTr(ds)}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {canUseAllMode && (
                    <div className="rexp-hint">
                      Tam export modu — her tablo için ayrı sayfa oluşturulur. Satır limitleri uygulanabilir.
                    </div>
                  )}
                </div>
              )}

              {/* İlerleme + geri sayım (async export) */}
              {exportLoading && (
                <div className="rexp-progress" role="status" aria-live="polite">
                  <div className="rexp-progress__row">
                    <span className="rexp-progress__msg">
                      {exportMessage || "Hazırlanıyor…"}
                    </span>
                    <span className="rexp-progress__pct">%{exportPercent}</span>
                  </div>
                  <div className="rexp-progress__bar">
                    <div
                      className="rexp-progress__fill"
                      style={{ width: `${Math.min(100, Math.max(3, exportPercent))}%` }}
                    />
                  </div>
                  <div className="rexp-progress__hint">
                    Rapor arka planda hazırlanıyor; büyük raporlarda birkaç dakika sürebilir.
                    {remainingLabel ? ` Kalan süre: ${remainingLabel}` : ""}
                  </div>
                </div>
              )}

              {/* Hata bloğu */}
              {exportError && (
                <div className="rexp-error-block">
                  <IconAlert />
                  <div>
                    <div className="rexp-error-block__title">İndirme Başarısız</div>
                    <div className="rexp-error-block__msg">{String(exportError)}</div>
                  </div>
                </div>
              )}

            </div>

            {/* ── Footer ── */}
            <div className="rexp-modal__footer">
              <button
                type="button"
                className="rexp-btn-cancel"
                onClick={closeModal}
                disabled={exportLoading}
              >
                Vazgeç
              </button>

              <button
                type="button"
                className="rexp-btn-confirm"
                onClick={onConfirm}
                disabled={confirmDisabled}
              >
                {exportLoading ? (
                  <>
                    <span className="rexp-spinner" aria-hidden="true" />
                    <span>{exportPercent > 0 ? `Hazırlanıyor… %${exportPercent}` : "Hazırlanıyor…"}</span>
                  </>
                ) : (
                  <>
                    <IconDownload />
                    <span>İndir</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}